import { useEffect } from 'react';

import { useLabelDragStore } from '../hooks/useLabelDragStore';

import type { LabelKind } from '@erikdakoda/label/getLabelKind';

type LabelDragSourceTrackerProps = {
  /** Kind of the section the drag started in. */
  kind: LabelKind;
};

/**
 * Publishes "a drag from this section is in flight" to the drag store.
 *
 * Rendered inside the section's `renderClone`, which dnd mounts only while that section is the
 * drag's home — mount and unmount therefore bracket the drag exactly.
 */
export function LabelDragSourceTracker({ kind }: LabelDragSourceTrackerProps): null {
  'use memo';

  const startDrag = useLabelDragStore((state) => state.startDrag);
  const endDrag = useLabelDragStore((state) => state.endDrag);

  useEffect(() => {
    startDrag(kind);
    return () => endDrag();
  }, [kind, startDrag, endDrag]);

  return null;
}

type LabelDropTargetTrackerProps = {
  kind: LabelKind;
  isOver: boolean;
};

/** Publishes the hovered section's kind so the drag preview can show the copy badge. */
export function LabelDropTargetTracker({ kind, isOver }: LabelDropTargetTrackerProps): null {
  'use memo';

  const setDestinationKind = useLabelDragStore((state) => state.setDestinationKind);

  useEffect(() => {
    if (!isOver) {
      return undefined;
    }
    setDestinationKind(kind);
    return () => setDestinationKind(null);
  }, [isOver, kind, setDestinationKind]);

  return null;
}

type LabelRemoveZoneTrackerProps = {
  /** True while the drag preview is over the remove zone. */
  isOver: boolean;
};

/** Publishes remove-zone hover so the drag preview can show the remove badge. */
export function LabelRemoveZoneTracker({ isOver }: LabelRemoveZoneTrackerProps): null {
  'use memo';

  const setOverRemoveZone = useLabelDragStore((state) => state.setOverRemoveZone);

  useEffect(() => {
    if (!isOver) {
      return undefined;
    }
    setOverRemoveZone(true);
    return () => setOverRemoveZone(false);
  }, [isOver, setOverRemoveZone]);

  return null;
}
